import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

function assertSessionPayload(data) {
  const token = typeof data?.token === "string" ? data.token.trim() : "";
  const user = data?.user;
  const role = typeof user?.role === "string" ? user.role.trim() : "";

  if (!token || !user || typeof user !== "object" || !role) {
    throw new Error("Oturum yanıtı doğrulanamadı. Lütfen tekrar deneyin.");
  }

  return { token, user };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | authed | anon

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus("anon");
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authed");
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const acceptSession = (data) => {
    const session = assertSessionPayload(data);
    localStorage.setItem(TOKEN_KEY, session.token);
    setUser(session.user);
    setStatus("authed");
  };

  const login = async (email, password, audience) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, audience });
      acceptSession(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const registerClient = async (payload) => {
    try {
      const { data } = await api.post("/auth/client/register", payload);
      acceptSession(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const loginWithGoogle = async (credential, permissions = {}) => {
    try {
      const { data } = await api.post("/auth/google", { credential, ...permissions });
      acceptSession(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Çıkış yerelde her durumda tamamlanır.
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus("anon");
  };

  return (
    <AuthContext.Provider value={{ user, status, login, registerClient, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
